import jsPDF from 'jspdf'
import { PDFDocument, PDFPage } from 'pdf-lib'
import { v4 as uuidv4 } from 'uuid'

interface Dimensions {
  width: number
  height: number
}

interface DimensionsAndRotation extends Dimensions {
  rotation: number // atm only 0 or -90 & w/ respect to bottom left corner of image
}

const A4_PAPER_DIMENSIONS = {
  width: 210,
  height: 297,
}

const A4_PAPER_RATIO = A4_PAPER_DIMENSIONS.width / A4_PAPER_DIMENSIONS.height

async function getImageElement(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve) => {
    const src = URL.createObjectURL(blob)
    const img = new Image()
    img.onload = function () {
      resolve(img)
    }
    img.src = src
  })
}

async function convertImageToPNG(blob: Blob): Promise<string> {
  const img = await getImageElement(blob)
  const canvas = document.createElement('canvas')
  canvas.width = img.width
  canvas.height = img.height
  const ctx = canvas.getContext('2d')!
  ctx.drawImage(img, 0, 0)

  const result = canvas.toDataURL('image/png')

  // clean up
  img.src = ''
  img.onload = null

  canvas.width = 0
  canvas.height = 0
  canvas.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height)

  return result
}

async function getImageDimension(imageFile: Blob): Promise<Dimensions> {
  return new Promise((resolve) => {
    const imgUrl = URL.createObjectURL(imageFile)
    const img = new Image()
    img.onload = function () {
      resolve({
        width: img.width,
        height: img.height,
      })
      URL.revokeObjectURL(imgUrl)
    }
    img.src = imgUrl
  })
}

function getImageDimensionOnA4Paper(imageDimension: Dimensions): DimensionsAndRotation {
  const imageRatio = imageDimension.width / imageDimension.height
  const shouldRotate = imageRatio > 1
  const rotation = shouldRotate ? -90 : 0 // rotations in PDFjs are performed against the bottom left corner of the image
  // we should only rotate if the image ratio is > 1.0 (i.e. width > height)
  // WITH ROTATION
  if (shouldRotate) {
    return {
      width: A4_PAPER_DIMENSIONS.height,
      height: A4_PAPER_DIMENSIONS.height / imageRatio,
      rotation,
    }
  }

  // WITHOUT ROTATION
  if (imageRatio > A4_PAPER_RATIO) {
    return {
      width: A4_PAPER_DIMENSIONS.width,
      height: A4_PAPER_DIMENSIONS.width / imageRatio,
      rotation,
    }
  } else {
    return {
      width: A4_PAPER_DIMENSIONS.height * imageRatio,
      height: A4_PAPER_DIMENSIONS.height,
      rotation,
    }
  }
}

async function getImageDimensionAndCoordinatesOnA4Paper(
  imageFile: Blob
): Promise<{ imageDimension: Dimensions; coordinates: { x: number; y: number }; rotation: number }> {
  const imgDimension = await getImageDimension(imageFile)
  const imgDimensionOnA4 = getImageDimensionOnA4Paper(imgDimension)
  const coordinates =
    imgDimensionOnA4.rotation === 0
      ? {
          x: (A4_PAPER_DIMENSIONS.width - imgDimensionOnA4.width) / 2,
          y: (A4_PAPER_DIMENSIONS.height - imgDimensionOnA4.height) / 2,
        }
      : {
          // these calculations are based on the image being rotated -90 degrees
          x: (A4_PAPER_DIMENSIONS.width - imgDimensionOnA4.height) / 2,
          y: -imgDimensionOnA4.height,
        }

  return {
    imageDimension: imgDimensionOnA4,
    coordinates,
    rotation: imgDimensionOnA4.rotation,
  }
}

function generatePdfFileName(file: File) {
  const { name: filename } = file
  return filename + '.pdf'
}

// the file is expected to be an image
export async function convertFromImageToPdf(file: File | Blob, filename?: string) {
  return convertFromImagesToPdf(
    [file],
    file instanceof File
      ? generatePdfFileName(file)
      : filename
      ? filename.replace('.pdf', '').concat('.pdf')
      : `${uuidv4()}.pdf`
  )
}

export async function createNewPdfPageWithMergedImages(images: Blob[]) {
  const pdf = new jsPDF()
  for (let index = 0; index < images.length; index++) {
    const image = images[index]
    const { imageDimension, coordinates, rotation } = await getImageDimensionAndCoordinatesOnA4Paper(image)
    const imageSrc = URL.createObjectURL(image)
    pdf.addImage(
      imageSrc,
      image.type,
      coordinates.x,
      coordinates.y,
      imageDimension.width,
      imageDimension.height,
      undefined,
      undefined,
      rotation
    )
  }
  const pdfArrayBuffer = pdf.output('arraybuffer')
  return new File([pdfArrayBuffer], 'Signed Document.pdf', { type: 'application/pdf' })
}

export async function convertFromImagesToPdf(files: Blob[], filename?: string) {
  const pdf = new jsPDF()
  const pdfFilename = filename || 'documents.pdf'

  for (let index = 0; index < files.length; index++) {
    const file = files[index]
    if (index > 0) pdf.addPage()
    const { imageDimension, coordinates, rotation } = await getImageDimensionAndCoordinatesOnA4Paper(file)
    const imageSrc = URL.createObjectURL(file)
    try {
      pdf.addImage(
        imageSrc,
        file.type,
        coordinates.x,
        coordinates.y,
        imageDimension.width,
        imageDimension.height,
        undefined,
        undefined,
        rotation
      )
    } catch (error) {
      // we will attempt to convert the image to a PNG since the original image byte code was rejected
      URL.revokeObjectURL(imageSrc)
      const pngSrc = await convertImageToPNG(file)
      pdf.addImage(
        pngSrc,
        file.type,
        coordinates.x,
        coordinates.y,
        imageDimension.width,
        imageDimension.height,
        undefined,
        undefined,
        rotation
      )
    }
  }
  const pdfArrayBuffer = pdf.output('arraybuffer')
  return new File([pdfArrayBuffer], pdfFilename, { type: 'application/pdf' })
}

export async function potentiallyConvertToPdf(file: File) {
  const { type } = file
  if (type.split('/')[0] === 'image') {
    return await convertFromImageToPdf(file)
  }
  return file
}

export async function mergePdfs(pdfs: Blob[]): Promise<ArrayBufferLike> {
  const arrayBuffers = await Promise.all(pdfs.map((it) => it.arrayBuffer()))
  const merged: PDFDocument = await PDFDocument.create()
  const resultPages: PDFPage[][] = await Promise.all(
    arrayBuffers.map(async (arrayBuffer) => {
      const pdf: PDFDocument = await PDFDocument.load(arrayBuffer)
      return await merged.copyPages(pdf, pdf.getPageIndices())
    })
  )
  resultPages.forEach((pageArray: PDFPage[]) => {
    pageArray.forEach((page) => {
      merged.addPage(page)
    })
  })
  const result = await merged.save()
  return result.buffer
}
